import cx from 'classnames'
import { useEffect, useState } from 'react'
import { GeneratedImage, ImageGenerateRequestGeneratedContent } from 'types'
import Styles from './GenerationSets.module.scss'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { AlertTriangle, Copy, MoreDots } from 'blaze-ui/icons'
import { Menu } from 'components/Menu/Menu'
import { Item, Section } from '@react-stately/collections'
import { GenerationSet, Request } from './GenerateImageDrawer'
import { Pressable } from '@react-aria/interactions'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { incompleteRequests } from './utils'
import { useFakeTimer } from './hooks/useFakeTimer'

const Image = ({
  requestId,
  generatedContent,
  setGenerationSets,
  onConfirm,
  onImagePress,
  onSaveImage,
  onDeleteImage,
  onGenerateMoreLikeThis,
  onRetry,
  aspectRatio,
  removeProcessingId,
}: {
  requestId: number
  generatedContent: string | null
  setGenerationSets: React.Dispatch<React.SetStateAction<GenerationSet[]>>
  onConfirm: (img: GeneratedImage) => void
  onImagePress: (img: GeneratedImage, requestId: number) => void
  onSaveImage: (img: GeneratedImage, requestId: number) => void
  onDeleteImage: (requestId: number) => void
  onGenerateMoreLikeThis: (requestId: number) => void
  onRetry: (requestId: number) => void
  aspectRatio: string
  removeProcessingId?: (id: number) => void
}) => {
  const [percentComplete, setPercentComplete] = useState(generatedContent ? 100 : 0)
  const [showError, setShowError] = useState(false)
  const { data } = usePollAISuggestionRequest({ id: requestId })
  const request = data?.request

  useEffect(() => {
    if (!request?.generated_content || generatedContent) return

    setGenerationSets((prev) => {
      return prev.map((set) => {
        if (set.requests.find((r) => r.id === requestId)) {
          const requests = set.requests.map((req) => {
            if (req.id === requestId) {
              return { ...req, generatedContent: request?.generated_content }
            }
            return req
          })
          return { ...set, requests }
        }

        return set
      })
    })
  }, [generatedContent, request?.generated_content, requestId, setGenerationSets])

  useEffect(() => {
    if (generatedContent || request?.generated_content || request?.status === 'successful') {
      setShowError(false)
    }
  }, [generatedContent, request?.generated_content, request?.status])

  useEffect(() => {
    if (request?.status === 'successful' || request?.status === 'failed') {
      removeProcessingId?.(requestId)
    }
  }, [request?.status, requestId, removeProcessingId])

  useFakeTimer({
    status: request?.status,
    generatedContent: request?.generated_content,
    setShowError,
    percentComplete,
    setPercentComplete,
  })

  const parseGeneratedImages = (content: string | null): ImageGenerateRequestGeneratedContent => {
    if (!content) return []
    try {
      const parsed = JSON.parse(content)
      if (Array.isArray(parsed)) return parsed
      if (parsed?.data && Array.isArray(parsed.data)) return parsed.data
    } catch (error) {
      // Fall through to return empty array.
    }
    return []
  }

  const generatedImages: ImageGenerateRequestGeneratedContent = parseGeneratedImages(generatedContent)
  const image = generatedImages[0]
  const hasImage = Boolean(image?.url)

  if (!hasImage && (request?.status === 'failed' || showError || request?.status === 'successful')) {
    return (
      <div className={cx(Styles.failedRequestContainer, Styles[aspectRatio])}>
        <div className={Styles.alertIconContainer}>
          {aspectRatio !== 'landscape' && <AlertTriangle size={40} />}
          <Text variant="metadata">Something went wrong. {aspectRatio === 'landscape' ? '' : 'Try again.'}</Text>
        </div>
        <Button size="xs" fullWidth onPress={() => request?.id && onRetry(request.id)}>
          Retry
        </Button>
      </div>
    )
  }

  if (!hasImage || percentComplete < 100) {
    return (
      <LoadingShimmerer>
        <div className={cx(Styles.placeholder, Styles[aspectRatio])} />
        <div className={Styles.progress}>
          <Text variant="smallList" color="var(--selected-fill)">
            {percentComplete}% Complete
          </Text>
        </div>
      </LoadingShimmerer>
    )
  }

  const handleAction = (key: React.Key) => {
    if (key === 'use-image') {
      onConfirm(image)
      return
    }
    if (key === 'generate-more-like-this') {
      onGenerateMoreLikeThis(requestId)
      return
    }
    if (key === 'save-image') {
      onSaveImage(image, requestId)
      return
    }
    if (key === 'delete-image') {
      onDeleteImage(requestId)
      return
    }
  }

  return (
    <Pressable onPress={() => onImagePress(image, requestId)}>
      <div className={Styles.preview}>
        <img src={image.url} alt={request?.prompt.topic} className={Styles.image} />
        <div className={Styles.moreButtonContainer}>
          <MenuTrigger placement="bottom" containFocus={false}>
            <IconButton
              size="xs"
              icon={MoreDots}
              variant="secondary"
              rounded
              data-testid={`more-button-${requestId}`}
            />
            <Menu
              aria-label="Image Options"
              itemClassName={Styles.menuItem}
              onAction={(key) => {
                handleAction(key)
              }}
              showCheckmark={false}
            >
              <Section>{[<Item key="use-image">Use Image</Item>]}</Section>
              <Section>{[<Item key="generate-more-like-this">Generate more like this</Item>]}</Section>
              <Section>{[<Item key="save-image">Save to Uploads</Item>]}</Section>
              <Section>{[<Item key="delete-image">Delete</Item>]}</Section>
            </Menu>
          </MenuTrigger>
        </div>
      </div>
    </Pressable>
  )
}

interface GenerationSetsProps {
  generationSets: GenerationSet[]
  setGenerationSets: React.Dispatch<React.SetStateAction<GenerationSet[]>>
  onConfirm: (img: GeneratedImage) => void
  onSaveImage: (img: GeneratedImage, requestId: number) => void
  onImagePress: (img: GeneratedImage, requestId: number) => void
  onDeleteImage: (requestId: number) => void
  onGenerateMoreLikeThis: (requestId: number) => void
  onRetry: (requestId: number) => void
  onCancelGeneration?: () => void
  removeProcessingId?: (id: number) => void
  numColumns?: 'two' | 'four'
  showProcessingText?: boolean
  processingRequestIds?: number[]
}

export const GenerationSets = ({
  generationSets,
  setGenerationSets,
  onConfirm,
  onSaveImage,
  onImagePress,
  onDeleteImage,
  onGenerateMoreLikeThis,
  onCancelGeneration,
  onRetry,
  removeProcessingId,
  numColumns = 'two',
  showProcessingText = false,
  processingRequestIds,
}: GenerationSetsProps) => {
  const processingText = (incompleteReqs: Request[]) => {
    if (incompleteReqs.length === 0) return 'Getting started...'

    return 'Creating images. This might take a moment...'
  }

  const removeGeneration = (uuid: string) => {
    setGenerationSets((prev) => prev.filter((set) => set.uuid !== uuid))
    onCancelGeneration?.()
  }

  return (
    <>
      {generationSets.map((generationSet, index) => {
        if (generationSet.requests.length === 0) return null

        return (
          <div key={`${generationSet.uuid}-${index}`} className={Styles.generationSet}>
            <div className={Styles.generationSetPrompt}>
              <Text color="var(--light-100)">{generationSet.promptDisplay}</Text>
              <IconButton
                icon={Copy}
                onPress={() => {
                  navigator.clipboard.writeText(generationSet.promptDisplay)
                  showToastMessage('Prompt copied to clipboard')
                }}
                color="var(--light-100)"
                variant="tertiary"
                rounded
              />
            </div>
            {showProcessingText &&
              (processingRequestIds
                ? generationSet.requests.some((request) => processingRequestIds.includes(request.id))
                : incompleteRequests(generationSet.requests).length > 0) && (
              <div className={Styles.processingContainer}>
                <span className={Styles.processingText}>
                  {processingText(
                    processingRequestIds
                      ? generationSet.requests.filter((request) => processingRequestIds.includes(request.id))
                      : incompleteRequests(generationSet.requests),
                  )}
                </span>
                <Button
                  size="sm"
                  variant="tertiary"
                  onPress={() => {
                    showToastMessage('Generation cancelled')
                    removeGeneration(generationSet.uuid)
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
            <div className={cx(Styles.imagesContainer, Styles[`${numColumns}Columns`])}>
              {generationSet.requests.map((request) => {
                return (
                  <Image
                    key={request.id}
                    setGenerationSets={setGenerationSets}
                    generatedContent={request?.generatedContent}
                    requestId={request.id}
                    onConfirm={onConfirm}
                    onImagePress={onImagePress}
                    onSaveImage={onSaveImage}
                    onDeleteImage={() => onDeleteImage(request.id)}
                    onGenerateMoreLikeThis={onGenerateMoreLikeThis}
                    onRetry={onRetry}
                    removeProcessingId={removeProcessingId}
                    aspectRatio={request?.prompt.image_aspect_ratio}
                  />
                )
              })}
            </div>
          </div>
        )
      })}
    </>
  )
}
